import { todayISO } from '@/lib/utils/date'
import type { ChallengeCategory, ChallengeEntry, ChallengePhase, ClubChallenge } from '@/types'

/** Where today falls relative to the challenge period */
export function challengePhase(
  challenge: Pick<ClubChallenge, 'starts_on' | 'ends_on'>,
  today = todayISO()
): ChallengePhase {
  if (today < challenge.starts_on) return 'upcoming'
  if (today > challenge.ends_on) return 'finished'
  return 'active'
}

/** Whether a book finished on `date` counts for the challenge */
export function isInPeriod(challenge: Pick<ClubChallenge, 'starts_on' | 'ends_on'>, date: string) {
  return date >= challenge.starts_on && date <= challenge.ends_on
}

/** Total categories to complete: the agreed target, or how many were suggested so far */
export function challengeTotal(
  challenge: Pick<ClubChallenge, 'target_categories'>,
  categoryCount: number
) {
  return Math.max(challenge.target_categories ?? 0, categoryCount)
}

/** How many categories each member is expected to suggest (e.g. 33 / 3 = 11) */
export function suggestionQuota(
  challenge: Pick<ClubChallenge, 'target_categories'>,
  memberCount: number
): number | null {
  if (!challenge.target_categories || memberCount === 0) return null
  return Math.ceil(challenge.target_categories / memberCount)
}

export interface MemberProgress {
  userId: string
  completed: number
  indicated: number
  suggested: number
}

/** Per member: completed entries, entries still being read, categories suggested */
export function memberProgress(
  memberIds: string[],
  categories: ChallengeCategory[],
  entries: ChallengeEntry[]
): MemberProgress[] {
  return memberIds
    .map((userId) => {
      const mine = entries.filter((e) => e.user_id === userId)
      return {
        userId,
        completed: mine.filter((e) => e.completed_on).length,
        indicated: mine.filter((e) => !e.completed_on).length,
        suggested: categories.filter((c) => c.suggested_by === userId).length,
      }
    })
    .sort((a, b) => b.completed - a.completed || b.indicated - a.indicated)
}

/** Lowercase, no accents, no punctuation, single spaces */
export function normalizeTitle(title: string) {
  return title
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function bigrams(text: string): Map<string, number> {
  const grams = new Map<string, number>()
  for (let i = 0; i < text.length - 1; i++) {
    const gram = text.slice(i, i + 2)
    grams.set(gram, (grams.get(gram) ?? 0) + 1)
  }
  return grams
}

/** Sørensen–Dice similarity on character bigrams, 0..1 */
export function titleSimilarity(a: string, b: string): number {
  const x = normalizeTitle(a)
  const y = normalizeTitle(b)
  if (!x || !y) return 0
  if (x === y) return 1
  const gx = bigrams(x)
  const gy = bigrams(y)
  let overlap = 0
  for (const [gram, count] of gx) overlap += Math.min(count, gy.get(gram) ?? 0)
  const total = x.length - 1 + (y.length - 1)
  return total > 0 ? (2 * overlap) / total : 0
}

/** Existing category that looks like the same idea (to warn before adding a duplicate) */
export function findSimilarCategory(
  title: string,
  categories: ChallengeCategory[],
  threshold = 0.75
): ChallengeCategory | null {
  if (normalizeTitle(title).length < 3) return null
  let best: { category: ChallengeCategory; score: number } | null = null
  for (const category of categories) {
    const score = titleSimilarity(title, category.title)
    if (score >= threshold && (!best || score > best.score)) best = { category, score }
  }
  return best?.category ?? null
}
